import { useCallback, useRef } from 'react';
import { useUndoRedo } from '@/lib/hooks/useUndoRedo';

interface CanvasLike {
  /** Fabric v6 `toJSON()` is typed without args; `toObject(props)` is the typed
   *  alternative and produces the same JSON-serializable shape. */
  toJSON(): object;
  toObject(propertiesToInclude?: string[]): object;
  loadFromJSON(json: object): Promise<void>;
  renderAll(): void;
}

/** Custom Fabric object props that must round-trip through toJSON /
 *  loadFromJSON. Exported so the IndexedDB session-save flow in EditorPage
 *  can use the same list (single source of truth). */
export const SERIALIZED_PROPS = [
  'layerId',
  'id',
  '_isBackground',
  'archValue',
  'globalCompositeOperation',
];

interface UseCanvasHistoryOptions<TExtra = unknown> {
  maxHistory?: number;
  /**
   * Optional invariant check fired right before each snapshot is recorded.
   * The hook still records the snapshot regardless — the validator's job
   * is purely diagnostic, surfacing recurring desync bugs (e.g. "canvas
   * has the Background image but the React layers list is empty, so the
   * next undo would wipe it") at the moment they're created instead of
   * minutes later when the user hits undo. Return a string describing
   * the problem, or `null` if everything looks consistent.
   */
  validateSnapshot?: (snapshot: { canvasJSON: object; extra?: TExtra }) => string | null;
}

/**
 * Optional extra-state hooks for snapshotting React state alongside the canvas
 * JSON. Used to capture/restore the layers list as part of undo/redo.
 */
export interface ExtraStateAccess<TExtra> {
  get: () => TExtra;
  set: (value: TExtra) => void;
}

interface Snapshot<TExtra = unknown> {
  canvasJSON: object;
  extra?: TExtra;
}

/**
 * Canvas-specific undo/redo built on top of the shared useUndoRedo hook.
 * Each snapshot stores the Fabric.js canvas JSON plus any optional extra
 * React state registered via `setExtraStateAccess`.
 */
export function useCanvasHistory<TExtra = unknown>({
  maxHistory = 50,
  validateSnapshot,
}: UseCanvasHistoryOptions<TExtra> = {}) {
  const canvasRef = useRef<CanvasLike | null>(null);
  const isRestoringRef = useRef(false);
  const extraAccessRef = useRef<ExtraStateAccess<TExtra> | null>(null);
  // Validator lives in a ref so callers can pass a closure over fresh
  // state without re-binding the hook's identity on every render.
  const validateSnapshotRef = useRef(validateSnapshot);
  validateSnapshotRef.current = validateSnapshot;

  const {
    canUndo,
    canRedo,
    undoCount,
    redoCount,
    snapshot,
    undo,
    redo,
    clear,
    getHistory,
    restore: restoreStack,
  } = useUndoRedo<Snapshot<TExtra>>({ maxHistory });

  const setCanvas = useCallback((canvas: CanvasLike | null) => {
    canvasRef.current = canvas;
  }, []);

  /**
   * Register getter/setter callbacks for additional React state to be
   * captured/restored alongside the canvas JSON. Stored in a ref so the
   * registration itself doesn't re-bind on every parent render.
   */
  const setExtraStateAccess = useCallback((access: ExtraStateAccess<TExtra> | null) => {
    extraAccessRef.current = access;
  }, []);

  /** Capture current canvas + extra state as a history snapshot */
  const saveSnapshot = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || isRestoringRef.current) return;
    const canvasJSON = canvas.toObject(SERIALIZED_PROPS);
    const extra = extraAccessRef.current?.get();

    // Invariant check — surface desync at snapshot time instead of
    // letting it lurk until the user hits undo. Validator failures
    // never block the snapshot (we'd rather record a wrong-but-recoverable
    // entry than drop the user's history); they're recorded as a console
    // error so the telemetry pipeline (`lib/telemetry.ts:162`) picks
    // them up alongside the breadcrumb stream.
    const validator = validateSnapshotRef.current;
    if (validator) {
      try {
        const problem = validator({ canvasJSON, extra });
        if (problem) {
          console.error(`[useCanvasHistory] snapshot invariant violation: ${problem}`, {
            canvasObjectCount: Array.isArray((canvasJSON as { objects?: unknown[] }).objects)
              ? (canvasJSON as { objects: unknown[] }).objects.length
              : 0,
            extra,
          });
        }
      } catch (err) {
        // A throwing validator is itself a bug worth surfacing, but we
        // never let it derail the snapshot — that's the user's escape
        // hatch from any subsequent state corruption.
        console.error('[useCanvasHistory] validateSnapshot threw', err);
      }
    }

    snapshot({ canvasJSON, extra });
  }, [snapshot]);

  /** Apply a single snapshot entry to the canvas + extra state. */
  const applyEntry = useCallback(async (entry: Snapshot<TExtra>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    isRestoringRef.current = true;
    try {
      await canvas.loadFromJSON(entry.canvasJSON);
      canvas.renderAll();
      if (entry.extra !== undefined && extraAccessRef.current) {
        extraAccessRef.current.set(entry.extra);
      }
    } finally {
      isRestoringRef.current = false;
    }
  }, []);

  /** Undo to previous state */
  const undoCanvas = useCallback(async () => {
    const prev = undo();
    if (prev) await applyEntry(prev);
  }, [undo, applyEntry]);

  /** Redo to next state */
  const redoCanvas = useCallback(async () => {
    const next = redo();
    if (next) await applyEntry(next);
  }, [redo, applyEntry]);

  /**
   * Restore the entire history stack from a persisted source (e.g. IndexedDB)
   * AND apply the snapshot at the given index to the canvas + extra state.
   */
  const restoreHistory = useCallback(
    async (states: Snapshot<TExtra>[], index: number) => {
      restoreStack(states, index);
      const current = states[index];
      if (current) await applyEntry(current);
    },
    [restoreStack, applyEntry],
  );

  return {
    setCanvas,
    setExtraStateAccess,
    saveSnapshot,
    undoCanvas,
    redoCanvas,
    clearHistory: clear,
    getHistory,
    restoreHistory,
    canUndo,
    canRedo,
    undoCount,
    redoCount,
    isRestoring: isRestoringRef,
  };
}
